import { useEffect, useMemo, useState } from 'react'

import { Card, Input } from 'antd'
import { faCheck, faLinkSimple, faMerge, faPen, faSave, faStethoscope, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { isFunction } from 'lodash'

import { Properties } from '../../../utils/entities'
import SectorsList from '../../../Components/shared/Lists/SectorsList'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import ConfirmableIconButton from '../../../Components/antd/Buttons/ConfirmableIconButton'
import ClickableIcon from '../../../Components/antd/Buttons/ClickableIcon'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { useSelector } from 'react-redux'
import { selectUser } from '../../../reducers/UserReducer'
import { isObject, validateString } from '../../../utils'
import { getInstitutionSectors } from '../../../utils/api/institution'
import IconLabel from '../../../Components/icons/IconLabel'
import { normalizeName } from '../../../utils/string'

const { NAME } = Properties

const SectorValidator = ({ sector, sectors = [], institution = {}, onValidate, onMerge, onSkip }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [selectedSector, setSelectedSector] = useState(null)
  const [institutionSectors, setInstitutionSectors] = useState([])
  const [loadingSectors, setLoadingSectors] = useState(false)
  const [sectorName, setSectorName] = useState(sector[NAME] ?? '')
  const [draftName, setDraftName] = useState(null)

  const editing = draftName !== null

  useEffect(() => {
    if (isObject(institution) && sectors.length === 0) {
      setLoadingSectors(true)

      getInstitutionSectors(user, institution).then(json => {
        if (json?.data) {
          setInstitutionSectors(json.data)
        }
      }).finally(() => setLoadingSectors(false))
    }
  }, [sectors.length, institution.id, user.id])

  const sectorsList = useMemo(() => sectors.length === 0 ? institutionSectors : sectors, [sectors, institutionSectors])
  /** A student names no service when he types a place in by hand : there is then nothing to create, only one to pick. */
  const hasSectorName = validateString(sectorName, { notEmpty: true }).valid

  const duplicate = useMemo(() => {
    const name = normalizeName(sectorName)

    return name ? sectorsList.find(s => normalizeName(s.name) === name) ?? null : null
  }, [sectorName, sectorsList])

  /** The service the institution already has is proposed as the merge target : the user only has to confirm it. */
  useEffect(() => {
    setSelectedSector(duplicate?.id ?? null)
  }, [duplicate?.id])

  const applyDraftName = () => {
    setSectorName(draftName.trim())
    setDraftName(null)
  }

  return (
    <div className='h-100 w-100 flex-row'>
      <div className='flex-column h-100 w-50 mr-8 jc-between'>
        <h3>{t('sector_validator.title.sector_to_validate')}</h3>
        <Card size='small' title={t('common.sector')}>
          {editing && (
            <div className='flex-row ai-ctr' style={{ gap: 4 }}>
              <Input autoFocus size='small' value={draftName} onChange={e => setDraftName(e.target.value)} onPressEnter={applyDraftName} />
              <ClickableIcon icon={faCheck} size='small' title={t('actions.save')} onClick={applyDraftName} />
              <ClickableIcon icon={faXmark} size='small' title={t('actions.cancel')} onClick={() => setDraftName(null)} />
            </div>
          )}
          {!editing && (
            <div className='flex-row ai-ctr jc-between' style={{ gap: 8 }}>
              <IconLabel icon={faStethoscope} text={hasSectorName ? sectorName : t('sector_validator.no_sector_proposed')} fontSize='small' />
              <ClickableIcon icon={faPen} size='small' title={t('common.edit')} onClick={() => setDraftName(sectorName)} />
            </div>
          )}
        </Card>
        {/** Creating a service the institution already has is still allowed, but never by accident. */}
        <ConfirmableIconButton
          className='mt-12'
          confirmation={isObject(duplicate) ? t('sector_validator.duplicate_confirmation', { sectorName: duplicate.name }) : null}
          disabled={loadingSectors || editing || !hasSectorName || (!!selectedSector && selectedSector !== duplicate?.id)}
          icon={faSave}
          text={t('sector_validator.validate_button')}
          type='primary'
          onClick={() => onValidate({ ...sector, [NAME]: sectorName })}
        />
      </div>
      <div className='flex-column h-100 w-50'>
        <h3>{t('sector_validator.title.sectors_list')}</h3>
        <SectorsList
          loading={loadingSectors}
          pinned={duplicate?.id}
          selected={selectedSector}
          sectors={sectorsList}
          onSelectedChange={setSelectedSector}
        />
        <IconButton
          className='mt-12'
          disabled={loadingSectors || !selectedSector}
          icon={faMerge}
          text={t('sector_validator.merge_button')}
          type='primary'
          onClick={() => onMerge({ id: selectedSector })}
        />
        {!hasSectorName && isFunction(onSkip) && (
          /** Nothing to create and nothing worth picking : the place alone still has to reach the request. */
          <IconButton
            className='mt-8'
            icon={faLinkSimple}
            text={t('sector_validator.skip_button')}
            onClick={onSkip}
          />
        )}
      </div>
    </div>
  )
}

export default SectorValidator
